// Default glassmorphism theme for SoftLavka AI ERP

import { createTheme, Theme } from '@mui/material/styles';
import { ThemeConfig } from '@/types';

export const defaultThemeConfig: ThemeConfig = {
  name: 'default',
  displayName: 'Cool Blue',
  colors: {
    primary: '#5B9BD5', // cool blue
    secondary: '#4A90E2', // bright cool blue
    background: 'linear-gradient(135deg, #0F1B2E 0%, #1A2332 25%, #1E3A5F 50%, #2E5090 75%, #3D6FB8 100%)',
    surface: 'rgba(30, 50, 80, 0.4)',
    text: '#E8F4F8', // light blue-white for readability
    textSecondary: 'rgba(232, 244, 248, 0.85)',
    border: 'rgba(91, 155, 213, 0.3)',
    success: '#4ECDC4', // cool teal
    warning: '#FFD93D', // yellow for warnings
    error: '#FF6B6B', // soft red
    info: '#6BA3D6', // light cool blue
  },
  glassmorphism: {
    background: 'rgba(30, 50, 80, 0.4)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(91, 155, 213, 0.3)',
    borderRadius: '20px',
    boxShadow: '0 12px 40px 0 rgba(15, 27, 46, 0.6)',
  },
};

export const createDefaultTheme = (): Theme => {
  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: defaultThemeConfig.colors.primary,
        light: '#7BB3E5',
        dark: '#3D7BB5',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: defaultThemeConfig.colors.secondary,
        light: '#6BA3E8',
        dark: '#2E6FC8',
        contrastText: '#FFFFFF',
      },
      background: {
        default: 'rgba(0, 0, 0, 0)',
        paper: defaultThemeConfig.colors.surface,
      },
      text: {
        primary: defaultThemeConfig.colors.text,
        secondary: defaultThemeConfig.colors.textSecondary,
      },
      success: {
        main: defaultThemeConfig.colors.success,
        light: '#7bc4fe',
        dark: '#3794e6',
      },
      warning: {
        main: defaultThemeConfig.colors.warning,
        light: '#69ed9b',
        dark: '#2fd163',
      },
      error: {
        main: defaultThemeConfig.colors.error,
        light: '#fb8bb4',
        dark: '#f85982',
      },
      info: {
        main: defaultThemeConfig.colors.info,
        light: '#8fa4f3',
        dark: '#4a5bb8',
      },
      divider: defaultThemeConfig.colors.border,
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 600,
        color: defaultThemeConfig.colors.text,
        textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        color: defaultThemeConfig.colors.text,
        textShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 500,
        color: defaultThemeConfig.colors.text,
        textShadow: '0 1px 4px rgba(0, 0, 0, 0.4)',
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 500,
        color: defaultThemeConfig.colors.text,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 500,
        color: defaultThemeConfig.colors.text,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 500,
        color: defaultThemeConfig.colors.text,
      },
      body1: {
        fontSize: '1rem',
        color: defaultThemeConfig.colors.text,
      },
      body2: {
        fontSize: '0.875rem',
        color: defaultThemeConfig.colors.textSecondary,
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 16,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: defaultThemeConfig.colors.background,
            minHeight: '100vh',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.5)',
              },
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            background: defaultThemeConfig.glassmorphism.background,
            backdropFilter: defaultThemeConfig.glassmorphism.backdropFilter,
            WebkitBackdropFilter: defaultThemeConfig.glassmorphism.backdropFilter,
            border: defaultThemeConfig.glassmorphism.border,
            borderRadius: defaultThemeConfig.glassmorphism.borderRadius,
            boxShadow: defaultThemeConfig.glassmorphism.boxShadow,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            background: 'rgba(30, 50, 80, 0.35)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(91, 155, 213, 0.25)',
            borderRadius: '12px',
            boxShadow: '0 6px 24px 0 rgba(15, 27, 46, 0.5)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 32px 0 rgba(15, 27, 46, 0.6)',
              border: '1px solid rgba(91, 155, 213, 0.4)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 500,
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '&.Mui-disabled': {
              opacity: 1,
            },
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            },
          },
          contained: {
            background: 'linear-gradient(135deg, #5B9BD5 0%, #4A90E2 100%)',
            color: '#FFFFFF',
            border: '1px solid rgba(91, 155, 213, 0.4)',
            boxShadow: '0 4px 16px rgba(91, 155, 213, 0.3)',
            '&.Mui-disabled': {
              color: '#ffffff',
              background: 'linear-gradient(135deg, #4f7fb4 0%, #5f8fc3 100%)',
              border: '1px solid rgba(91, 155, 213, 0.35)',
              boxShadow: 'none',
            },
            '&:hover': {
              background: 'linear-gradient(135deg, #6BA3D6 0%, #5B9BD5 100%)',
              boxShadow: '0 6px 24px rgba(91, 155, 213, 0.4)',
            },
          },
          outlined: {
            background: 'rgba(30, 50, 80, 0.3)',
            border: '1px solid rgba(91, 155, 213, 0.4)',
            color: defaultThemeConfig.colors.text,
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.75)',
              border: '1px solid rgba(255, 255, 255, 0.28)',
              background: 'rgba(30, 50, 80, 0.35)',
            },
            '&:hover': {
              background: 'rgba(91, 155, 213, 0.15)',
              border: '1px solid rgba(91, 155, 213, 0.6)',
            },
          },
          text: {
            color: defaultThemeConfig.colors.text,
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.72)',
            },
            '&:hover': {
              background: 'rgba(91, 155, 213, 0.1)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              borderRadius: '12px',
              '& fieldset': {
                border: '1px solid rgba(255, 255, 255, 0.3)',
              },
              '&:hover fieldset': {
                border: '1px solid rgba(255, 255, 255, 0.5)',
              },
              '&.Mui-focused fieldset': {
                border: '2px solid rgba(102, 126, 234, 0.8)',
              },
              '& input': {
                color: '#ffffff',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              '&.Mui-focused': {
                color: '#667eea',
              },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            background: 'rgba(30, 50, 80, 0.3)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: '12px',
            '& .MuiOutlinedInput-notchedOutline': {
              border: '1px solid rgba(91, 155, 213, 0.3)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: '1px solid rgba(91, 155, 213, 0.5)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '2px solid rgba(91, 155, 213, 0.8)',
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            background: 'rgba(30, 50, 80, 0.5)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(91, 155, 213, 0.3)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px 0 rgba(15, 27, 46, 0.6)',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: defaultThemeConfig.colors.text,
            '&:hover': {
              background: 'rgba(91, 155, 213, 0.15)',
            },
            '&.Mui-selected': {
              background: 'rgba(91, 155, 213, 0.25)',
              '&:hover': {
                background: 'rgba(91, 155, 213, 0.35)',
              },
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: defaultThemeConfig.glassmorphism.background,
            backdropFilter: defaultThemeConfig.glassmorphism.backdropFilter,
            WebkitBackdropFilter: defaultThemeConfig.glassmorphism.backdropFilter,
            border: defaultThemeConfig.glassmorphism.border,
            boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.3)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: 'rgba(30, 50, 80, 0.5)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(91, 155, 213, 0.3)',
            borderRadius: '0 20px 20px 0',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: '#ffffff',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            background: 'rgba(30, 50, 80, 0.4)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(91, 155, 213, 0.3)',
            borderRadius: '12px',
            color: defaultThemeConfig.colors.text,
          },
        },
      },
    },
  });
};




